import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import ConfirmDialogActions, {
  confirmDialogActionsSx,
  confirmDialogActionsWithSecondarySx,
} from '../common/ConfirmDialogActions';
import SplitPaymentFields from '../common/SplitPaymentFields';
import { fieldLabelSx } from '../common/formFieldStyles';
import { formatCurrency, DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import {
  createDefaultSplitPaymentLines,
  toCheckoutPayload,
  toSplitAmount,
  validateSplitPaymentLines,
} from '../../utils/splitPaymentUtils';
import { getPendingProductSaleLines } from '../../utils/appointmentCheckoutUtils';
import AppointmentProductUsageSection from './AppointmentProductUsageSection';
import { BRAND } from '../../theme/brandTokens';

const AppointmentCheckoutDialog = ({
  open,
  onClose,
  onConfirm,
  onSecondaryConfirm,
  confirming = false,
  title,
  confirmText,
  secondaryConfirmText,
  currencyCode = DEFAULT_CURRENCY,
  membershipFee = 0,
  amountDue = 0,
  children,
  appointment,
  allowDiscount = false,
  productUsage = null,
  canSeeCosts = false,
}) => {
  const { t } = useTranslation();
  const discountableDue = Math.max(0, toSplitAmount(amountDue));
  const planFee = Math.max(0, toSplitAmount(membershipFee));
  const totalDue = Math.round((discountableDue + planFee) * 100) / 100;
  const pendingProductLines = useMemo(() => getPendingProductSaleLines(appointment), [appointment]);
  const [lines, setLines] = useState(() => createDefaultSplitPaymentLines(totalDue));
  const [errors, setErrors] = useState({});
  const [usageLines, setUsageLines] = useState(productUsage?.lines || []);
  const [usageEdited, setUsageEdited] = useState(false);
  const hasSecondaryConfirm = Boolean(secondaryConfirmText && onSecondaryConfirm);
  const showPayments = totalDue > 0;

  useEffect(() => {
    if (!open) {
      return;
    }

    setLines(createDefaultSplitPaymentLines(totalDue));
    setErrors({});
    setUsageLines(productUsage?.lines || []);
    setUsageEdited(false);
  }, [open, totalDue, productUsage]);

  const withUsage = (payload) => {
    if (!usageEdited) {
      return payload;
    }

    return {
      ...payload,
      consumedProducts: usageLines.map((line) => ({
        productId: line.productId,
        quantity: Number(line.quantity) || 0,
      })),
    };
  };

  const submitCheckout = (bookNext) => {
    if (!showPayments) {
      const payload = withUsage({});
      if (bookNext) {
        onSecondaryConfirm(payload);
        return;
      }
      onConfirm(payload);
      return;
    }

    const result = validateSplitPaymentLines({
      lines,
      amountDue: totalDue,
      allowDiscount,
    });

    setErrors(result.errors);
    if (!result.isValid) {
      return;
    }

    const payload = withUsage({
      ...toCheckoutPayload({
        payments: result.payments,
        collected: result.collected,
        amountDue: totalDue,
      }),
      ...(pendingProductLines.length ? { customerProductLines: pendingProductLines } : {}),
    });

    if (bookNext) {
      onSecondaryConfirm(payload);
      return;
    }

    onConfirm(payload);
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={confirming ? undefined : onClose}
      maxWidth="md"
      title={title}
      dividers
      showCloseButton
      hideCloseOnXs
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={hasSecondaryConfirm ? confirmDialogActionsWithSecondarySx : confirmDialogActionsSx}
      actions={(
        <ConfirmDialogActions
          onCancel={onClose}
          cancelText={t('common.cancel')}
          onConfirm={() => submitCheckout(false)}
          confirmText={confirming ? t('common.saving') : confirmText}
          onSecondaryConfirm={hasSecondaryConfirm ? () => submitCheckout(true) : undefined}
          secondaryConfirmText={secondaryConfirmText}
          loading={confirming}
        />
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {showPayments && planFee > 0 ? (
          <Box>
            <Typography variant="subtitle2" sx={fieldLabelSx}>
              {t('appointmentCheckout.membershipFee')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.primaryText }}>
              {formatCurrency(planFee, currencyCode)}
            </Typography>
          </Box>
        ) : null}

        {showPayments ? (
          <Box>
            <Typography variant="subtitle2" sx={fieldLabelSx}>
              {t('appointmentCheckout.amountDue')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatCurrency(totalDue, currencyCode)}
            </Typography>
          </Box>
        ) : null}

        {showPayments ? (
          <SplitPaymentFields
            lines={lines}
            onChange={setLines}
            amountDue={totalDue}
            currencyCode={currencyCode}
            allowDiscount={allowDiscount}
            disabled={confirming}
            errors={errors}
          />
        ) : null}

        <AppointmentProductUsageSection
          lines={usageLines}
          currency={currencyCode}
          canSeeCosts={canSeeCosts}
          onChange={(nextLines, edited) => {
            setUsageLines(nextLines);
            setUsageEdited(edited);
          }}
        />

        {children}
      </Box>
    </ScrollableDialog>
  );
};

export default AppointmentCheckoutDialog;
